import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, Landmark } from "lucide-react";
import { BancaUpload } from "@/components/banche/BancaUpload";
import { CollegaDialog } from "@/components/banche/CollegaDialog";
import { MovimentoRiga, MovimentoCard } from "@/components/banche/MovimentoRiga";
import { fmtEur, MEZZI, STATI } from "@/lib/banche";

const EMPTY = { q: "", da: "", a: "", tipo: "", mezzo: "", banca: "", stato: "", imp_min: "", imp_max: "" };
const Sel = ({ value, onChange, children, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid={testid}>{children}</select>
);
const Kpi = ({ label, value, cls, testid }) => (
  <Card className="card-elevated"><CardContent className="p-4"><div className="text-xs text-muted-foreground">{label}</div><div className={`text-xl font-extrabold tabular-nums ${cls}`} data-testid={testid}>{value}</div></CardContent></Card>
);

export default function Banche() {
  const [f, setF] = useState(() => ({ ...EMPTY, stato: new URLSearchParams(window.location.search).get("stato") || "" }));
  const [d, setD] = useState(null);
  const [mov, setMov] = useState(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
    try { setD((await api.get("/banche/movimenti", { params })).data); } catch { toast.error("Caricamento movimenti non riuscito"); }
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const onMezzo = async (m, mezzo) => { try { await api.patch(`/banche/movimenti/${m.id}`, { mezzo }); load(); } catch { toast.error("Modifica non riuscita"); } };
  const onScollega = async (m) => {
    if (!window.confirm("Annullare il collegamento? Il pagamento creato verrà tolto dalla fattura.")) return;
    try { await api.post(`/banche/movimenti/${m.id}/scollega`); toast.success("Collegamento annullato"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };
  const rowProps = { onCollega: setMov, onScollega, onMezzo };

  return (
    <div className="space-y-5 pb-20 md:pb-0" data-testid="banche-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Landmark className="h-8 w-8 text-primary" />Banche</h1>
          <p className="text-sm text-muted-foreground">Movimenti di tutte le banche, collegati alle fatture.</p></div>
        <BancaUpload onDone={load} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi label="Entrate" value={fmtEur(d?.entrate)} cls="text-emerald-700" testid="banche-entrate" />
        <Kpi label="Uscite" value={fmtEur(d?.uscite)} cls="text-red-600" testid="banche-uscite" />
        <Kpi label="Da collegare" value={d?.da_collegare ?? "—"} cls="text-amber-700" testid="banche-dacollegare" />
      </div>
      <Card className="card-elevated"><CardContent className="p-3 space-y-2">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input className="pl-8 h-9" placeholder="Cerca descrizione o controparte…" value={f.q} onChange={(e) => set("q")(e.target.value)} data-testid="banche-search" /></div>
          <Button size="sm" variant={f.stato === "da_collegare" ? "default" : "outline"} onClick={() => set("stato")(f.stato === "da_collegare" ? "" : "da_collegare")} data-testid="banche-filtro-dacollegare">Da collegare</Button>
          <Button size="sm" variant="ghost" onClick={() => setF(EMPTY)} data-testid="banche-reset">Azzera filtri</Button>
        </div>
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <Input type="date" className="h-9 w-auto" value={f.da} onChange={(e) => set("da")(e.target.value)} data-testid="banche-da" />
          <Input type="date" className="h-9 w-auto" value={f.a} onChange={(e) => set("a")(e.target.value)} data-testid="banche-a" />
          <Sel value={f.tipo} onChange={set("tipo")} testid="banche-tipo"><option value="">Entrate e uscite</option><option value="entrate">Entrate</option><option value="uscite">Uscite</option></Sel>
          <Sel value={f.mezzo} onChange={set("mezzo")} testid="banche-mezzo"><option value="">Tutti i mezzi</option>{Object.entries(MEZZI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</Sel>
          <Sel value={f.banca} onChange={set("banca")} testid="banche-banca"><option value="">Tutte le banche</option>{(d?.banche || []).map((b) => <option key={b} value={b}>{b}</option>)}</Sel>
          <Sel value={f.stato} onChange={set("stato")} testid="banche-stato"><option value="">Tutti gli stati</option>{Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</Sel>
          <Input type="number" step="0.01" className="h-9 w-28" placeholder="Da €" value={f.imp_min} onChange={(e) => set("imp_min")(e.target.value)} data-testid="banche-imp-min" />
          <Input type="number" step="0.01" className="h-9 w-28" placeholder="A €" value={f.imp_max} onChange={(e) => set("imp_max")(e.target.value)} data-testid="banche-imp-max" />
        </div>
      </CardContent></Card>
      {!d ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : d.movimenti.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-10" data-testid="banche-empty">Nessun movimento. Carica un estratto conto per iniziare.</div>
      ) : (
        <>
          <Card className="card-elevated hidden md:block"><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="banche-table">
              <thead className="bg-muted/50 text-xs text-muted-foreground"><tr>{["Data", "Banca", "Descrizione", "Controparte", "Mezzo", "Importo", "Stato", ""].map((h) => <th key={h} className={`px-3 py-2 font-semibold ${h === "Importo" ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
              <tbody>{d.movimenti.map((m) => <MovimentoRiga key={m.id} m={m} {...rowProps} />)}</tbody>
            </table>
          </CardContent></Card>
          <div className="md:hidden space-y-2">{d.movimenti.map((m) => <MovimentoCard key={m.id} m={m} {...rowProps} />)}</div>
          {d.totale > d.movimenti.length && <div className="text-xs text-center text-muted-foreground">Mostrati {d.movimenti.length} di {d.totale}: restringi i filtri per vedere gli altri.</div>}
        </>
      )}
      <CollegaDialog mov={mov} onClose={() => setMov(null)} onDone={load} />
    </div>
  );
}
